import { FrameRange, WireObject } from 'content-creation-common/models';

import { SHOT_LIST_THUMBNAIL_SIZE } from '../features/video-reformatting/constants';
import { MESSAGES } from '../features/video-reformatting/messages';

import Frame from '../model/Frame';

import { getThumbnailUrl } from '../util/mediaUtils';
import { isNumber } from '../util/numbers';
import {
  deepEqual,
  isBoolean,
  isEmpty as isEmptyObject,
  isNotNil,
  isNil,
} from '../util/object';
import MediaSegmentEdit from './media/MediaSegmentEdit';
import { isEmpty as isEmptyArray } from '../util/array';

/**
 * A Shot represents a segment of an episode. It has a start frame and an end frame.
 */
export default class Shot extends WireObject {
  _bestSuggestedEdit;
  _endFrame;
  _id;
  _frameRange;
  _isAnalysisComplete;
  _mediaId;
  _posterImageUrl;
  _startFrame;
  _suggestedEdits = {};
  _rawSuggestedEdits; // @todo
  _thumbnailUrl;

  /**
   * @typedef {Object} shotWireObject
   * @param {number} end_frame
   * @param {id} id  Video Shot Id
   * @param {id} media_id
   * @param {boolean} shot_analysis_complete
   * @param {number} start_frame
   * @param {Object<MEDIA_SEGMENT_EDIT_TYPE, MediaSegmentEditWireObject>} suggested_edits
   */

  /**
   * @function
   * @param  {shotWireObject} shotWireObject
   * @return {Shot}
   */
  constructor(
    id,
    mediaId,
    startFrame,
    endFrame,
    isAnalysisComplete = false,
    suggestedEdits = undefined,
  ) {
    super();

    if (isNumber(endFrame)) {
      this._endFrame = new Frame(endFrame);
    }

    if (isNotNil(id)) {
      this._id = id;
    }

    if (isNumber(startFrame)) {
      this._startFrame = new Frame(startFrame);
    }

    if (isNumber(endFrame) && isNumber(startFrame)) {
      this._frameRange = new FrameRange(startFrame, endFrame);
    }

    if (isBoolean(isAnalysisComplete)) {
      this._isAnalysisComplete = isAnalysisComplete;
    }

    if (isNotNil(mediaId)) {
      this._mediaId = mediaId;

      const frameNumber = startFrame || 0;
      this._posterImageUrl = getThumbnailUrl(mediaId, {
        frameNumber,
      });

      this._thumbnailUrl = getThumbnailUrl(mediaId, {
        frameNumber,
        width: SHOT_LIST_THUMBNAIL_SIZE,
      });
    }
    if (isNotNil(suggestedEdits)) {
      this._rawSuggestedEdits = suggestedEdits;
      const suggestedEditsEntries = Object.entries(suggestedEdits);
      let maxConfidence = 0;
      for (const [editType, editWireObject] of suggestedEditsEntries) {
        const mediaSegmentEdit = new MediaSegmentEdit(editType, editWireObject);
        this._suggestedEdits[editType] = mediaSegmentEdit;
        if (mediaSegmentEdit.confidence >= maxConfidence) {
          this._bestSuggestedEdit = mediaSegmentEdit;
          maxConfidence = mediaSegmentEdit.confidence;
        }
      }
    }
  }

  /**
   * @function equals
   * @param {Shot} inShot
   * @return {boolean}
   */
  equals(inShot) {
    if (!Shot.isShot(inShot)) return false;

    return (
      isNotNil(this.endFrame) &&
      this.endFrame.equals(inShot.endFrame) &&
      isNotNil(this.frameRange) &&
      this.frameRange.equals(inShot.frameRange) &&
      this.id === inShot.id &&
      this.isAnalysisComplete === inShot.isAnalysisComplete &&
      this.mediaId === inShot.mediaId &&
      this.posterImageUrl === inShot.posterImageUrl &&
      isNotNil(this.startFrame) &&
      this.startFrame.equals(inShot.startFrame) &&
      this.thumbnailUrl === inShot.thumbnailUrl &&
      deepEqual(this.rawSuggestedEdits, inShot.rawSuggestedEdits)
    );
  }

  /**
   * @function containsFrameNumber
   * @param  {number} frameNumber
   * @return {boolean} checks whether or not the frameNumber overlaps the shot frame range
   */
  containsFrameNumber(frameNumber) {
    return (
      this.startFrame.frameNumber <= frameNumber &&
      this.endFrame.frameNumber >= frameNumber
    );
  }

  /**
   * @function bestSuggestedEdit
   * @return {MediaSegmentEdit}
   */
  get bestSuggestedEdit() {
    return this._bestSuggestedEdit;
  }

  /**
   * @function endFrame
   * @return {Frame}
   */
  get endFrame() {
    return this._endFrame;
  }

  /**
   * @function
   * @return {id} Shot id
   */
  get id() {
    return this._id;
  }

  /**
   * @function frameMarker
   * @return {string} Human readable time for the start frame
   */
  get frameMarker() {
    return isNotNil(this.startFrame)
      ? this.startFrame.frameMarker
      : MESSAGES.UNKNOWN_FRAME_MARKER;
  }

  /**
   * @function frameRange
   * @return {FrameRange}
   */
  get frameRange() {
    return this._frameRange;
  }

  /**
   * @function isAnalysisComplete
   * @return {boolean}
   */
  get isAnalysisComplete() {
    return this._isAnalysisComplete;
  }

  /**
   * @function
   * @return {id}
   */
  get mediaId() {
    return this._mediaId;
  }

  /**
   * @function posterImageUrl
   * @return {string}
   */
  get posterImageUrl() {
    return this._posterImageUrl;
  }

  /**
   * @function rawSuggestedEdits
   * @return {object}
   */
  get rawSuggestedEdits() {
    return this._rawSuggestedEdits;
  }

  /**
   * @function startFrame
   * @return {Frame}
   */
  get startFrame() {
    return this._startFrame;
  }

  /**
   * @function suggestedEdits
   * @return {MediaSegmentEdit[]}
   */
  get suggestedEdits() {
    return this._suggestedEdits;
  }

  /**
   * @function thumbnailUrl
   * @return {string}
   */
  get thumbnailUrl() {
    return this._thumbnailUrl;
  }

  copy() {
    return new Shot(
      this.id,
      this.mediaId,
      this.frameRange.start,
      this.frameRange.end,
      this.isAnalysisComplete,
      this.rawSuggestedEdits,
    );
  }

  toJSON() {
    return {
      end_frame: this.frameRange.end,
      id: this.id,
      media_id: this.mediaId,
      shot_analysis_complete: this.isAnalysisComplete,
      start_frame: this.frameRange.start,
      suggested_edits: this.rawSuggestedEdits,
    };
  }

  static fromJSON(inJSON) {
    if (isEmptyObject(inJSON)) {
      throw new Error('Shot.fromJSON inJSON is nil');
    }

    const {
      end_frame: endFrame,
      id,
      media_id: mediaId,
      shot_analysis_complete: isAnalysisComplete,
      start_frame: startFrame,
      suggested_edits: suggestedEdits,
    } = inJSON;

    return new Shot(
      id,
      mediaId,
      startFrame,
      endFrame,
      isAnalysisComplete,
      suggestedEdits,
    );
  }

  /**
   * Checks if object is an instance of Shot
   *
   * @param inputObject
   * @returns {boolean}
   */
  static isShot(inputObject) {
    return inputObject instanceof Shot;
  }

  /**
   * Get the center X for a shot. Try to get the center X from the edit suggestion.
   * Otherwise, the center x will be defaulted to 0 (center).
   */
  static getCenterX(shot, mediaSegmentEditType, mediaSegmentEditViewIndex) {
    if (isNil(shot) || isNil(shot.suggestedEdits)) return 0;

    const suggestedEdit = shot.suggestedEdits[mediaSegmentEditType];
    if (
      isNil(suggestedEdit) ||
      isEmptyArray(suggestedEdit.views) ||
      suggestedEdit.views.length <= mediaSegmentEditViewIndex
    ) {
      return 0;
    }

    const mediaSegmentEditView = suggestedEdit.views[mediaSegmentEditViewIndex];
    if (
      isNil(mediaSegmentEditView) ||
      !isNumber(mediaSegmentEditView.startCenterX)
    ) {
      return 0;
    }

    return mediaSegmentEditView.startCenterX;
  }
}
